/**
 * 用户状态管理 Store
 *
 * 功能:
 * - 管理用户登录状态
 * - 存储用户信息（用户名、邮箱、头像等）
 * - 处理登录、注册、注销
 * - 支持第三方登录（Google、微信等）
 * - 用户个人资料管理（修改密码、头像等）
 *
 * 数据持久化:
 * - 用户信息保存到 localStorage
 * - 登录状态标记
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { API_BASE_URL } from '@/config/api'
import {
  loginWithEmail,
  loginWithPhone,
  loginWithEmailCode,
  loginWithPhoneCode,
  sendVerificationCode as apiSendVerificationCode,
  updateUsername as apiUpdateUsername,
  updateAvatar as apiUpdateAvatar,
  checkBindPhone,
  checkBindEmail,
  bindPhone,
  bindEmail,
  sendVerificationCode as apiRequestBindCode,
  updatePassword,
  deleteUser,
  validatePhone,
  validateEmail,
  type ApiResponse,
  getErrorMessage,
  handleThirdPartyCallback,
  type UserInfo as ApiUserInfo,
} from '@/api/auth'

/**
 * 用户信息类型（重新导出）
 */
export type UserInfo = ApiUserInfo

function isEnglishLocale(): boolean {
  try {
    const savedSource = localStorage.getItem('app_language_source')
    const lang = localStorage.getItem('app_language')?.replace('-', '_').toLowerCase()
    if (savedSource === 'user' && lang) return lang.startsWith('en')
    return (navigator.language || '').toLowerCase().startsWith('en')
  } catch {
    return false
  }
}

function authMessage(zh: string, en: string): string {
  return isEnglishLocale() ? en : zh
}

/**
 * 用户 Store 定义
 */
export const useUserStore = defineStore('user', () => {
  // State
  const userInfo = ref<UserInfo | null>(null)
  const isLoggedIn = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const username = computed(() => userInfo.value?.username || '')
  const avatar = computed(() => userInfo.value?.avatar || '')
  const uuid = computed(() => userInfo.value?.uuid || '')

  // Actions
  function loadFromStorage(): void {
    const stored = localStorage.getItem('userInfo')
    const loggedIn = localStorage.getItem('isLoggedIn')

    if (stored && loggedIn === 'true') {
      try {
        userInfo.value = JSON.parse(stored)
        isLoggedIn.value = true
      } catch {
        clearUserData()
      }
    }
  }

  function setUser(user: UserInfo): void {
    userInfo.value = user
    isLoggedIn.value = true

    // Persist to localStorage
    localStorage.setItem('userInfo', JSON.stringify(user))
    localStorage.setItem('isLoggedIn', 'true')
    localStorage.setItem('uuid', user.uuid || '')
    localStorage.setItem('username', user.username || '')
    localStorage.setItem('authType', user.authType || '')
    localStorage.setItem('email', user.email || '')
    localStorage.setItem('phone', user.phone || '')
    localStorage.setItem('google', user.google || '')
    localStorage.setItem('wechat', user.wechat || '')
    if (user.avatar) {
      localStorage.setItem('avatar', user.avatar)
    } else {
      localStorage.removeItem('avatar')
    }
  }

  function clearUserData(): void {
    userInfo.value = null
    isLoggedIn.value = false

    // Clear localStorage
    localStorage.removeItem('userInfo')
    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('uuid')
    localStorage.removeItem('username')
    localStorage.removeItem('avatar')
    localStorage.removeItem('authType')
    localStorage.removeItem('email')
    localStorage.removeItem('google')
    localStorage.removeItem('wechat')
    localStorage.removeItem('phone')
  }

  function logout(): void {
    clearUserData()
  }

  async function updateAvatar(avatarUrl: string): Promise<void> {
    if (userInfo.value) {
      userInfo.value.avatar = avatarUrl
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
      localStorage.setItem('avatar', avatarUrl)
    }
  }

  async function updateUsername(name: string): Promise<void> {
    if (userInfo.value) {
      userInfo.value.username = name
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
      localStorage.setItem('username', name)
    }
  }

  async function updateUsernameRemote(name: string): Promise<{ success: boolean; message: string }> {
    if (!userInfo.value?.uuid) {
      return { success: false, message: '用户未登录' }
    }

    try {
      const response = await apiUpdateUsername(userInfo.value.uuid, name)
      if (response.code === 1) {
        await updateUsername(name)
        return { success: true, message: getErrorMessage(1) }
      }
      return { success: false, message: getErrorMessage(response.code, response.message) }
    } catch {
      return { success: false, message: '修改用户名失败' }
    }
  }

  async function updateAvatarRemote(imageBlob: Blob): Promise<{ success: boolean; message: string; avatarUrl?: string }> {
    if (!userInfo.value?.uuid) {
      return { success: false, message: '用户未登录' }
    }

    try {
      const response = await apiUpdateAvatar(userInfo.value.uuid, imageBlob)
      if (response.code === 1 && response.data) {
        const avatarUrl = String(response.data)
        await updateAvatar(avatarUrl)
        return { success: true, message: getErrorMessage(1), avatarUrl }
      }
      return { success: false, message: getErrorMessage(response.code, response.message) }
    } catch {
      return { success: false, message: '头像更新失败' }
    }
  }

  async function requestBindCode(contact: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiRequestBindCode(contact)
      if (response.code === 1) {
        return { success: true, message: getErrorMessage(1) }
      }
      return { success: false, message: getErrorMessage(response.code, response.message) }
    } catch {
      return { success: false, message: '发送验证码失败' }
    }
  }

  async function bindContact(contact: string, code: string): Promise<{ success: boolean; message: string }> {
    if (!userInfo.value?.uuid) {
      return { success: false, message: '用户未登录' }
    }

    const isPhone = validatePhone(contact)
    const isEmail = validateEmail(contact)
    if (!isPhone && !isEmail) {
      return { success: false, message: '请输入正确的手机号或邮箱' }
    }

    try {
      let checkResponse: ApiResponse
      let bindResponse: ApiResponse
      if (isPhone) {
        checkResponse = await checkBindPhone(userInfo.value.uuid, contact, code)
        if (checkResponse.code !== 1) {
          return { success: false, message: getErrorMessage(checkResponse.code, checkResponse.message) }
        }
        bindResponse = await bindPhone(userInfo.value.uuid, contact, code)
      } else {
        checkResponse = await checkBindEmail(userInfo.value.uuid, contact, code)
        if (checkResponse.code !== 1) {
          return { success: false, message: getErrorMessage(checkResponse.code, checkResponse.message) }
        }
        bindResponse = await bindEmail(userInfo.value.uuid, contact, code)
      }

      if (bindResponse.code === 1) {
        if (userInfo.value) {
          if (isPhone) userInfo.value.phone = contact
          if (isEmail) userInfo.value.email = contact
          localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
          if (isPhone) localStorage.setItem('phone', contact)
          if (isEmail) localStorage.setItem('email', contact)
        }
        return { success: true, message: getErrorMessage(1) }
      }

      return { success: false, message: getErrorMessage(bindResponse.code, bindResponse.message) }
    } catch {
      return { success: false, message: '绑定失败，请稍后重试' }
    }
  }

  async function updatePasswordRemote(params: {
    account: string
    code: string
    newPassword: string
  }): Promise<{ success: boolean; message: string }> {
    if (!userInfo.value?.uuid) {
      return { success: false, message: '用户未登录' }
    }

    const isPhone = validatePhone(params.account)
    const isEmail = validateEmail(params.account)
    if (!isPhone && !isEmail) {
      return { success: false, message: '请输入正确的手机号或邮箱' }
    }

    try {
      const payload: {
        uuid: string
        password: string
        user_code: string
        phone?: string
        email?: string
      } = {
        uuid: userInfo.value.uuid,
        password: params.newPassword,
        user_code: params.code,
      }
      if (isPhone) payload.phone = params.account
      if (isEmail) payload.email = params.account

      const response = await updatePassword(payload)
      if (response.code === 1) {
        return { success: true, message: getErrorMessage(1) }
      }
      return { success: false, message: getErrorMessage(response.code, response.message) }
    } catch {
      return { success: false, message: '修改密码失败，请稍后重试' }
    }
  }

  async function deleteAccountRemote(): Promise<{ success: boolean; message: string }> {
    if (!userInfo.value?.uuid) {
      return { success: false, message: '用户未登录' }
    }

    try {
      const response = await deleteUser(userInfo.value.uuid)
      if (response.code === 1) {
        clearUserData()
        return { success: true, message: getErrorMessage(1) }
      }
      return { success: false, message: getErrorMessage(response.code, response.message) }
    } catch {
      return { success: false, message: '注销账号失败，请稍后重试' }
    }
  }

  // Login with email/phone and password
  async function login(credential: string, password: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      const isEmail = credential.includes('@')
      const response = isEmail
        ? await loginWithEmail(credential, password)
        : await loginWithPhone(credential, password)

      if (response.code === 1 && response.data) {
        setUser(response.data)
        return true
      } else {
        error.value = getErrorMessage(response.code)
        return false
      }
    } catch (e) {
      error.value = authMessage('登录失败，请检查账号密码', 'Login failed. Please check your account and password')
      return false
    } finally {
      loading.value = false
    }
  }

  // Login with verification code
  async function loginWithCode(credential: string, code: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      const isEmail = credential.includes('@')
      const response = isEmail
        ? await loginWithEmailCode(credential, code)
        : await loginWithPhoneCode(credential, code)

      if (response.code === 1 && response.data) {
        setUser(response.data)
        return true
      } else {
        error.value = getErrorMessage(response.code)
        return false
      }
    } catch (e) {
      error.value = authMessage('登录失败，请检查验证码', 'Login failed. Please check the verification code')
      return false
    } finally {
      loading.value = false
    }
  }

  // Register new user with code verification
  async function register(data: {
    username: string
    email?: string
    phone?: string
    password: string
    code: string
  }): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      // Registration uses same endpoint as code login
      const isEmail = !!data.email
      const credential = data.email || data.phone || ''

      const response = isEmail
        ? await loginWithEmailCode(credential, data.code)
        : await loginWithPhoneCode(credential, data.code)

      if (response.code === 1 && response.data) {
        // Update username after registration if needed
        const user = response.data
        if (data.username && data.username !== user.username) {
          // Note: username update would require separate API call
          user.username = data.username
        }
        setUser(user)
        return true
      } else {
        error.value = getErrorMessage(response.code)
        return false
      }
    } catch (e) {
      error.value = authMessage('注册失败，请稍后重试', 'Registration failed. Please try again later')
      return false
    } finally {
      loading.value = false
    }
  }

  // Send verification code
  async function sendVerificationCode(contact: string): Promise<boolean> {
    try {
      const response = await apiSendVerificationCode(contact)
      return response.code === 1
    } catch {
      return false
    }
  }

  // Handle third-party login callback
  function handleThirdPartyLoginCallback(): boolean {
    const result = handleThirdPartyCallback()
    if (result.success && result.userInfo) {
      setUser(result.userInfo)
      return true
    }
    if (result.error) {
      error.value = result.error
    }
    return false
  }

  // Get third-party login URL
  async function getThirdPartyLoginUrl(type: 'google' | 'wechat'): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/${type}`)
      const data = await response.json()
      if (data.code === 1 && data.data) {
        return data.data
      }
      return null
    } catch {
      return null
    }
  }

  return {
    // State
    userInfo,
    isLoggedIn,
    loading,
    error,
    // Getters
    username,
    avatar,
    uuid,
    // Actions
    loadFromStorage,
    setUser,
    clearUserData,
    logout,
    updateAvatar,
    updateUsername,
    updateUsernameRemote,
    updateAvatarRemote,
    requestBindCode,
    bindContact,
    updatePasswordRemote,
    deleteAccountRemote,
    login,
    loginWithCode,
    register,
    sendVerificationCode,
    handleThirdPartyLoginCallback,
    getThirdPartyLoginUrl,
  }
})
